import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameState } from '@pacmates/engine';
import type { PublicRoomView } from '../wire';
import type { StoredSession } from '../storage';
import type { ConnStatus } from '../useRoomSocket';
import { GameCanvas } from '../game/GameCanvas';
import { HUD } from '../game/HUD';
import { DPad } from '../game/DPad';
import { Joystick } from '../game/Joystick';
import { attachKeyboard } from '../game/keyboard';
import { useMediaQuery } from '../useMediaQuery';
import { useGameLayout, CONTROL_SIZE_PREFERRED_BTN } from '../game/useGameLayout';
import { CountdownOverlay } from '../components/CountdownOverlay';
import { LevelClearOverlay } from '../components/LevelClearOverlay';
import { didLevelClear } from '../game/levelClear';
import { PauseOverlay } from '../components/PauseOverlay';
import { PauseMenuButton } from '../components/PauseMenuButton';
import { SwipeHint } from '../components/SwipeHint';
import { HelpModal } from '../components/HelpModal';
import { SettingsModal } from '../components/SettingsModal';
import { DisconnectBanner } from '../components/DisconnectBanner';
import { Loading } from '../components/Loading';
import { MuteButton } from '../components/MuteButton';
import { MissMarkerButton } from '../components/MissMarkerButton';
import { isTraceEnabled } from '../game/trace';
import { detectSoundEvents } from '../game/soundEvents';
import { sound } from '../game/sound';
import { useSettings } from '../settings';
import { Announcer } from '../components/Announcer';
import { computeAnnouncement } from '../game/announcements';

export interface GameScreenProps {
  session: StoredSession;
  lobby: PublicRoomView | null;
  status: 'lobby' | 'playing' | 'paused' | 'over';
  state: GameState | null;
  prevState: GameState | null;
  receivedAt: number;
  connStatus: ConnStatus;
  onSendInput: (dir: 'up' | 'down' | 'left' | 'right' | 'none') => void;
  onSendInputP2?: (dir: 'up' | 'down' | 'left' | 'right' | 'none') => void;
  onPause: () => void;
  onResume: () => void;
  onLeave: () => void;
  localTwoPlayer: boolean;
}

const SNAPSHOT_INTERVAL_MS = 50; // server broadcasts at >= 20 Hz (room.ts)

// TURN-INTENT TRACE: capture-build only -- see main.tsx's TRACE_BUILD doc
// comment. Gates the marker button render below `TRACE_BUILD &&
// isTraceEnabled() && ...` (not `isTraceEnabled()` alone) so a plain/prod
// build can dead-code-eliminate the whole branch, including the
// `MissMarkerButton` import, rather than leaving it present-but-unrendered.
const TRACE_BUILD = import.meta.env.VITE_ENABLE_TRACE === '1';

export function GameScreen({
  session,
  lobby,
  status,
  state,
  prevState,
  receivedAt,
  connStatus,
  onSendInput,
  onSendInputP2,
  onPause,
  onResume,
  onLeave,
  localTwoPlayer,
}: GameScreenProps): JSX.Element {
  const isTouchLayout = useMediaQuery('(pointer: coarse), (max-width: 900px)');
  const [showCountdown, setShowCountdown] = useState(true);
  // PART 3: the level just displayed by LevelClearOverlay, or null when no
  // banner is showing. `null` (not a boolean) so the banner's own text
  // always shows the level that WAS cleared into, not whatever `state.level`
  // has drifted to by the time the timeout fires.
  const [levelClearBanner, setLevelClearBanner] = useState<number | null>(null);
  // PART 2: latest a11y announcement, or null. Same "latest wins, shallow
  // queue" idiom the doc comment on computeAnnouncement describes -- a new
  // snapshot's message always REPLACES whatever was there, never appends.
  const [announcement, setAnnouncement] = useState<string | null>(null);
  const sawPlayingRef = useRef(false);
  const [settings] = useSettings();
  // Modals stacked ON TOP of the pause menu (opened from its Settings/How
  // to Play buttons) -- owned here rather than by PauseOverlay itself so
  // they can outlive a Resume/Leave from underneath if that ever mattered,
  // and so `attachKeyboard` below can see whether one is open.
  const [activeModal, setActiveModal] = useState<'help' | 'settings' | null>(null);

  // Plain `useRef` mutating `.current` does NOT cause a re-render, so an
  // effect that reads it can permanently miss a node that attaches on a
  // LATER render (this component has an early "Loading..." return before
  // `state` arrives -- see below -- whose tree doesn't mount the stage at
  // all). State-backed callback refs make attach/detach a real state
  // change that useGameLayout can depend on (see its own comment for the
  // full story -- this was the actual root cause of BUGLEDGER.md #4).
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const [topStackEl, setTopStackEl] = useState<HTMLDivElement | null>(null);
  const [dpadDockEl, setDpadDockEl] = useState<HTMLDivElement | null>(null);
  const stageRefCb = useCallback((el: HTMLDivElement | null) => setStageEl(el), []);
  const topStackRefCb = useCallback((el: HTMLDivElement | null) => setTopStackEl(el), []);
  const dpadDockRefCb = useCallback((el: HTMLDivElement | null) => setDpadDockEl(el), []);
  // 'joystick' is the default touch control (see settings.ts ControlScheme);
  // 'dpad' keeps the classic 4-button pad as an option; 'swipe' shows
  // neither (canvas swipe, GameCanvas's onSwipe below, is always live
  // regardless of this setting). Both on-screen controls share the same
  // dock element/ref (dpadDockEl/dpadDockRefCb) and the same
  // useGameLayout.ts maze-protecting size budget -- see .joystick-dock /
  // .dpad-dock in index.css, which both consume the identical `--dpad-btn`
  // measured unit.
  const controlScheme = settings.controlScheme;
  const showTouchControl = isTouchLayout && controlScheme !== 'swipe';
  const preferredDpadBtn = CONTROL_SIZE_PREFERRED_BTN[settings.controlSize];
  const layout = useGameLayout(
    stageEl,
    topStackEl,
    dpadDockEl,
    isTouchLayout,
    state?.cols,
    state?.rows,
    preferredDpadBtn,
  );

  useEffect(() => {
    if (status === 'playing' && !sawPlayingRef.current) {
      sawPlayingRef.current = true;
      setShowCountdown(true);
    }
  }, [status]);

  useEffect(() => {
    const cleanup = attachKeyboard({
      mode: localTwoPlayer ? 'local2p' : 'single',
      onDir: onSendInput,
      onDirP2: onSendInputP2,
      onPause: () => {
        if (status === 'playing') onPause();
        else if (status === 'paused') onResume();
      },
      // Disabled while a Help/Settings modal is stacked on top of the
      // pause menu -- otherwise Esc (already used by that modal's own
      // focus trap to close ITSELF) would also resume the game
      // underneath, and arrow/WASD keys would fire game input while the
      // player is actually interacting with a settings control.
      enabled: (status === 'playing' || status === 'paused') && activeModal === null,
    });
    return cleanup;
  }, [status, onSendInput, onSendInputP2, onPause, onResume, localTwoPlayer, activeModal]);

  // Sound events: fire exactly once per genuinely NEW authoritative
  // snapshot -- keyed ONLY on `receivedAt` (snapshot identity), never on
  // `status`. `status` is read through a ref instead of a dependency: if it
  // were a dependency, a pause/resume toggle (which changes `status` but
  // NOT `receivedAt`, since no new snapshot necessarily arrived) would
  // re-run this effect against the SAME already-processed snapshot pair and
  // replay stale sounds. The ref always reflects the latest status without
  // forcing a re-run.
  const statusRef = useRef(status);
  statusRef.current = status;
  useEffect(() => {
    if (statusRef.current !== 'playing' || !state) return;
    for (const event of detectSoundEvents(prevState, state)) {
      sound.play(event);
    }
    // Deliberately keyed on snapshot identity only -- see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedAt]);

  // PART 3: level-clear banner. Same "keyed on snapshot identity only"
  // idiom as the sound-events effect just above (and for the identical
  // reason) -- a pause/resume toggle changes `status` but not `receivedAt`
  // (no new snapshot necessarily arrived), and re-running this against the
  // SAME already-processed snapshot pair would re-fire the banner on every
  // unrelated re-render. Firing strictly on `roundClearedCount` INCREASING
  // (didLevelClear) rather than e.g. `state.level` changing keeps this tied
  // to the one authoritative signal SPEC.md §1.1 designates for "a level
  // was actually cleared", not a proxy that could coincidentally change for
  // another reason.
  useEffect(() => {
    if (!state || !prevState) return;
    if (didLevelClear(prevState, state)) {
      setLevelClearBanner(state.level);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedAt]);

  // PART 2: a11y announcements. Exact same "keyed on snapshot identity via
  // receivedAt, status read through a ref" idiom as the sound-events effect
  // above (and for the identical reason -- a pause/resume toggle must not
  // re-fire this against the same already-processed snapshot pair).
  useEffect(() => {
    if (statusRef.current !== 'playing' || !state) return;
    const msg = computeAnnouncement(prevState, state, session.seat);
    if (msg) setAnnouncement(msg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedAt]);

  if (!state) {
    return (
      <div className="screen game-screen" data-testid="screen-game">
        <Loading label={connStatus === 'reconnecting' ? 'Reconnecting...' : 'Loading game...'} />
        <button type="button" className="btn btn-link" onClick={onLeave} data-testid="btn-leave">
          Leave
        </button>
      </div>
    );
  }

  const seats = lobby?.seats;
  const p1Name = seats?.[0]?.name ?? 'Player 1';
  const p2Name = seats?.[1]?.name ?? 'Player 2';
  const p2Connected = seats?.[1]?.connected ?? false;
  const partnerIdx = session.seat === 1 ? 1 : 0;
  const partnerDisconnected = seats ? !seats[partnerIdx]!.connected && !!seats[partnerIdx]!.name : false;
  // P2 is "real" iff same-device 2P is active, or a networked player has
  // actually joined seat 2 -- otherwise the engine's parked P2 pac + empty
  // P2 badge is pure display noise for a solo round. Plain props, so this
  // is reactive: a networked join mid-game flips it live.
  const p2Active = localTwoPlayer || !!seats?.[1]?.name;

  return (
    <div className="screen game-screen" data-testid="screen-game" ref={stageRefCb}>
      <Announcer message={announcement} />
      <MuteButton />
      {isTouchLayout && status === 'playing' && <PauseMenuButton onOpen={onPause} />}
      <div className="stage-top" ref={topStackRefCb}>
        <DisconnectBanner selfDisconnected={connStatus !== 'open'} partnerDisconnected={partnerDisconnected} />
        <HUD
          state={state}
          p1Name={p1Name}
          p2Name={p2Name}
          p2Connected={p2Connected}
          showP2={p2Active}
          mySeat={session.seat}
          colorblind={settings.colorblind}
        />
        {/* TOP-10 #4 fix (mobile-320 toast overlapping the maze,
            BUGLEDGER-class real occlusion defect): this used to render
            OUTSIDE `.stage-top` as an absolute overlay specifically so it
            would never inflate the measured top-stack height -- but that is
            exactly what let it float independent of the HUD and land on top
            of the canvas at the narrowest breakpoint. It is now a real flow
            child of `.stage-top`, the SAME pattern `.hud-buff-row` already
            uses successfully (see that rule's own comment): useGameLayout's
            ResizeObserver on `topStackEl` picks up its real height
            automatically the instant it mounts/unmounts, so the maze canvas
            budget shrinks by its real height while it's shown and grows
            back the moment it's dismissed/times out -- it now RESERVES
            layout space instead of floating over the play area, at every
            breakpoint including 320px. */}
        {isTouchLayout && status === 'playing' && <SwipeHint />}
        {/* TURN-INTENT TRACE: debug-only, see MissMarkerButton.tsx's own
            doc comment for why this is a REAL flow child of `.stage-top`
            (never overlaps the joystick/maze, by construction) rather than
            an absolute overlay. Only visible with tracing enabled + a live
            round -- zero DOM/layout impact for every ordinary session. */}
        {TRACE_BUILD && isTraceEnabled() && status === 'playing' && <MissMarkerButton code={session.code} seat={session.seat} />}
      </div>
      <div className="game-body" data-orientation={layout.orientation}>
        <GameCanvas
          state={state}
          prevState={prevState}
          receivedAt={receivedAt}
          snapshotIntervalMs={SNAPSHOT_INTERVAL_MS}
          onSwipe={(d) => onSendInput(d)}
          width={layout.ready ? layout.canvasW : undefined}
          height={layout.ready ? layout.canvasH : undefined}
          drawP2={p2Active}
          p1Name={p1Name}
          p2Name={p2Name}
          mySeat={session.seat}
          colorblind={settings.colorblind}
          reducedMotionSetting={settings.reducedMotion}
        />
        {showTouchControl && (
          <div
            className={controlScheme === 'joystick' ? 'joystick-dock' : 'dpad-dock'}
            ref={dpadDockRefCb}
            style={{ '--dpad-btn': `${layout.dpadBtn}px` } as React.CSSProperties}
          >
            {controlScheme === 'joystick' ? (
              <Joystick onDir={(d) => onSendInput(d)} disabled={status !== 'playing'} />
            ) : (
              <DPad onDir={(d) => onSendInput(d)} disabled={status !== 'playing'} />
            )}
          </div>
        )}
      </div>
      {status === 'paused' && (
        <PauseOverlay
          onResume={onResume}
          canResume={connStatus === 'open'}
          onOpenSettings={() => setActiveModal('settings')}
          onOpenHelp={() => setActiveModal('help')}
          onLeave={onLeave}
          code={session.code}
          pausedBy={lobby?.pausedBy ?? null}
          mySeat={session.seat}
          partnerName={session.seat === 1 ? p2Name : p1Name}
        />
      )}
      {status === 'playing' && showCountdown && <CountdownOverlay onDone={() => setShowCountdown(false)} />}
      {status === 'playing' && levelClearBanner !== null && (
        <LevelClearOverlay level={levelClearBanner} onDone={() => setLevelClearBanner(null)} />
      )}
      {activeModal === 'help' && <HelpModal onClose={() => setActiveModal(null)} />}
      {activeModal === 'settings' && <SettingsModal onClose={() => setActiveModal(null)} />}
    </div>
  );
}
